<template>
    <v-container fluid>
      <v-card elevation="2">
        <!-- ——— header / toolbar ——— -->
        <v-card-title class="d-flex align-center tableheader white--text">
          <v-icon left color="white">mdi-wrench-clock</v-icon>
          Maintenance Requests
          <v-spacer/>
          <v-text-field
            v-model="search"
            label="Search (description, status, unit)"
            prepend-inner-icon="mdi-magnify"
            density="compact"
            variant="solo-filled"
            single-line flat hide-details
            class="me-4"
          />
          <v-btn color="primary" @click="openDialog(null)" v-if="authStore.canEdit">
            <v-icon left>mdi-plus</v-icon> Add Request
          </v-btn>
          <!-- <ExportButtons entity="maintenance_requests" filename="Maintenance Requests" :exclude="['id','created_at','updated_at']"></ExportButtons> -->
          <ExportButtons entity="maintenance_requests" filename="Maintenance Requests" :headers="headers"></ExportButtons>
        </v-card-title>
  
        <!-- ——— data table ——— -->
        <v-data-table :items="filtered" :headers="headers" class="elevation-1">
          <template v-slot:[`item.unit_id`]="{ value }">
            {{ value ? findName(units, value) : '—' }}
          </template>

          <template v-slot:[`item.tenant_id`]="{ value }">
            {{ findTenantName(tenants, value) }}
          </template>

          <template v-slot:[`item.priority`]="{ value }">
            {{ formPriorities(value) }}
          </template>

          <template v-slot:[`item.vendor_id`]="{ value }">
            {{ value ? findName(vendors, value) : '—' }}
          </template>

          <template v-slot:[`item.status`]="{ value }">
            {{ formStatuses(value) }}
          </template>

          <template v-slot:[`item.estimated_cost`]="{ value }">
            {{ formatCurrency(value) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip location="top" text="View Details">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="openViewDialog(item)" color="info">
                mdi-eye-outline
              </v-icon>
            </template>
          </v-tooltip>

          <v-tooltip location="top" text="Edit">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="openDialog(item)" color="secondary" v-if="authStore.canEdit">mdi-pencil</v-icon>
            </template>
          </v-tooltip>

          <v-tooltip location="top" text="Delete">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small @click="deleteItemPrompt(item)" color="error" v-if="authStore.isAdmin">mdi-delete</v-icon>
            </template>
          </v-tooltip>
          </template>
          <template v-slot:[`no-data`]>
            <v-alert :value="true" color="info" icon="mdi-information-variant" class="ma-3">
              {{ tableLoading ? 'Fetching data...' : 'No maintenance requests available or match your search.' }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card>
  
      <!-- ——— add / edit dialog ——— -->
      <v-dialog v-model="dialog" max-width="600">
        <v-card>
          <v-card-title>{{ form.id ? 'Edit' : 'Add' }} Request</v-card-title>
          <v-form ref="maintenanceRequestForm" v-model="isFormValid" @submit.prevent="save">  
          <v-card-text class="pt-0">
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  :items="units"
                  item-title="name"
                  item-value="id"
                  v-model="form.unit_id"
                  label="Unit*"
                  variant="outlined"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  :items="tenants"
                  item-title="fullName"
                  item-value="id"
                  v-model="form.tenant_id"
                  label="Tenant*"
                  variant="outlined"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
            </v-row>
  
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  :items="priorities"
                  item-title="text"
                  item-value="value"
                  v-model="form.priority"
                  label="Priority*"
                  variant="outlined"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  :items="statuses"
                  item-title="text"
                  item-value="value"
                  v-model="form.status"
                  label="Status*"
                  variant="outlined"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
            </v-row>
  
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  type="date"
                  v-model="form.request_date"
                  label="Date*"
                  variant="outlined"
                  :rules="[rules.required, rules.validDate]"
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  :items="vendors"
                  item-title="name"
                  item-value="id"
                  v-model="form.vendor_id"
                  label="Vendor*"
                  clearable
                  variant="outlined"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
            </v-row>
  
            <v-textarea
              v-model="form.description"
              label="Description*"
              auto-grow
              rows="2"
              variant="outlined"
              :rules="[rules.required]"
              required
            />
  
            <v-text-field
              v-model="form.estimated_cost"
              type="number"
              label="Est. Cost*"
              :prefix="getCurrencyPrefix()"
              variant="outlined"
              :rules="[rules.required]"
              required
            />

            <!-- <v-select
              v-model="form.company_id"
              :items="companies"
              item-title="name"
              item-value="id"
              label="Company"
              variant="outlined"
              class="mb-2"
              :rules="[rules.required]"
            /> -->
          </v-card-text>
  
          <v-card-actions>
            <v-spacer/>
            <v-btn text @click="dialog = false">Cancel</v-btn>
            <v-btn color="primary" variant="elevated" type="submit" :loading="dialogLoading" :disabled="!isFormValid">Save</v-btn>
          </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>

      <v-dialog v-model="viewDialog" max-width="800px" @update:modelValue="val => !val && closeViewDialog()">
      <v-card :loading="viewDialogLoading">
        <v-card-title class="tableheader white--text d-flex align-center">
          <v-icon left color="white" class="mr-2">mdi-wrench-clock</v-icon>
          <span class="text-h5">Request Details: {{ viewItem?.description }}</span>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" color="white" @click="closeViewDialog"></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-if="viewItem" class="pt-4">
          <v-list lines="two" density="compact">
            <!-- <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">ID:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.id }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider> -->
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Unit:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findName(units, viewItem.unit_id) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Tenant:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findTenantName(tenants, viewItem.tenant_id) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Date:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatDateForDisplay(viewItem.request_date) || 'N/A' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Description:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.description || 'N/A' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Priority:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formPriorities(viewItem.priority) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Description:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.description || 'N/A' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Vendor:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findName(vendors, viewItem.vendor_id) || 'N/A' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Est. Cost:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatCurrency(viewItem.estimated_cost) || 'N/A' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Status:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formStatuses(viewItem.status) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>

            <!-- <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Company:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findName(companies, viewItem.company_id) || 'N/A' }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider> -->
          </v-list>
        </v-card-text>
        <v-card-text v-else class="text-center py-5">
            <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
            <p>Loading unit details...</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="bg-grey-lighten-4 pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="tonal" @click="closeViewDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card :loading="dialogLoading">
        <v-card-title class="text-h5 error--text">Confirm Deletion</v-card-title>
        <v-card-text>Are you sure you want to delete maintenance request: <strong>{{ itemToDelete?.description }}</strong>?</v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" text @click="closeDeleteDialog" :disabled="dialogLoading">Cancel</v-btn>
            <v-btn color="error" @click="confirmDelete" :loading="dialogLoading">Delete</v-btn>
            <v-spacer></v-spacer>
        </v-card-actions>
        </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
    </v-container>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, nextTick } from 'vue';
  import MaintenanceRequestService from '@/services/MaintenanceRequestService';
  import UnitService      from '@/services/UnitService';
  import TenantService    from '@/services/TenantService';
  import VendorService    from '@/services/VendorService';
  import { useAuthStore } from '@/stores/auth';
  import { formatDateForDisplay, formatCurrency, getCurrencyPrefix, useSnackbar } from '@/services/genFunctions';
  import ExportButtons from '@/components/ExportButtons.vue';
  // import CompanyService from '@/services/CompaniesService';
  // const companies = ref([]);

  const { snackbar, showSnackbar } = useSnackbar();
  const authStore = useAuthStore();
  /* ——— reactive state ——— */
  const mrs      = ref([]);
  const units    = ref([]);
  const tenants  = ref([]);
  const vendors  = ref([]);
  const dialogLoading = ref(false);
  // const editedItem = ref(null);

  const search   = ref('');
  const dialog   = ref(false);
  const form     = ref(resetForm());
  const viewDialogLoading = ref(false);
  const viewDialog = ref(false);
  const viewItem = ref(null);
  const isFormValid = ref(false);
  const deleteDialog = ref(false);
  const itemToDelete = ref(null);
  const maintenanceRequestForm = ref(null);

  const rules = {
    required: value => !!value || 'This field is required.',
    positiveNumber: value => (typeof value === 'number' && value > 0) || 'Must be a positive number > 0.',
    validDate: value => /^\d{4}-\d{2}-\d{2}$/.test(value || '') || 'Invalid date format (YYYY-MM-DD).',
    validDateOptional: value => (!value || /^\d{4}-\d{2}-\d{2}$/.test(value)) || 'Invalid date format (YYYY-MM-DD).',
  };

  const statuses = [
    { text: 'Open', value: 'open' },
    { text: 'In Progress', value: 'in_progress' },
    { text: 'Closed', value: 'closed' }
  ];
  
  const formStatuses = (status) => {
    return statuses.find(s => s.value === status)?.text || 'N/A';
  };

  const priorities = [
    { text: 'Low', value: 'low' },
    { text: 'Medium', value: 'medium' },
    { text: 'High', value: 'high' }
  ];

  const formPriorities = (priority) => {
    return priorities.find(p => p.value === priority)?.text || 'N/A';
  };

  const headers = [
    { title: 'Unit',    key: 'unit_id'     },
    { title: 'Tenant',  key: 'tenant_id'   },
    { title: 'Date',    key: 'request_date', width: '110' },
    { title: 'Priority',key: 'priority',   width: '90'  },
    { title: 'Status',  key: 'status',     width: '110' },
    { title: 'Est. Cost',    key: 'estimated_cost',       width: '90'  },
    { title: 'Vendor',  key: 'vendor_id'   },
    { title: 'Actions', key: 'actions',    sortable:false, width:'130' }
  ];
  
  /* ——— lifecycle ——— */
  // const loadCompanies = async () => companies.value = await CompanyService.getCompanies();
  
  onMounted(async () => {
    // await loadCompanies();
    await loadAll();
  });

  async function loadAll () {
    [mrs.value, vendors.value] = await Promise.all([//units.value, tenants.value, 
      MaintenanceRequestService.getAll(),
      // UnitService.getUnits(),
      // TenantService.getTenants(),
      VendorService.getVendors()
    ]);

    fetchDropdownData();
  }

  const fetchDropdownData = async () => {
    try {
      const tenantsData = await TenantService.getTenants();
      tenants.value = tenantsData.map(t => ({ ...t, fullName: `${t.first_name} ${t.last_name}` }));
  
      const unitsData = await UnitService.getUnits();
      units.value = unitsData;
    } catch (error) {
      console.error("Error fetching form data for maintenance requests:", error);
      showSnackbar('Failed to load tenant/unit data for form.', 'error');
    }
  };

  /* ——— helpers ——— */
  function resetForm () {
    return {
      id: null, unit_id: null, tenant_id: null, request_date: new Date().toISOString().substr(0,10),
      description:'', priority:'low', status:'open', cost:'', vendor_id:null
    };
  }
  
  function findName(list, id) {
    const obj = list.find(i => i.id === id);
    return obj ? obj.name : id;
  }
  
  function findTenantName (list, id) {
  const obj = list.find(i => i.id === id);
  return obj
    ? (obj.fullName || obj.name || `${obj.first_name ?? ''} ${obj.last_name ?? ''}`.trim())
    : '—';   // graceful fallback
  }

  const filtered = computed(() => {
    const q = search.value.toLowerCase();
    return mrs.value.filter(r =>
      (r.description||'').toLowerCase().includes(q) ||
      r.status.toLowerCase().includes(q) ||
      String(r.unit_id).includes(q)
    );
  });

  /* ——— crud ——— */
  function openDialog(item) {
    form.value = item ? { ...item } : resetForm();
    dialog.value = true;
  }
  
  async function save () {
    if (!maintenanceRequestForm.value) return;
    const { valid } = await maintenanceRequestForm.value.validate();
    if (!valid) {
      showSnackbar('Please correct the form errors.', 'warning');
      return;
    }
    const { id, ...data } = form.value;
    id ? await MaintenanceRequestService.update(id, data)
       : await MaintenanceRequestService.create(data);
    dialog.value = false;
    await loadAll();
    showSnackbar('Maintenance request saved successfully!', 'success');
  }

  const deleteItemPrompt = (item) => {
    itemToDelete.value = { ...item };
    deleteDialog.value = true;
  };

  const closeDeleteDialog = () => {
    deleteDialog.value = false;
    nextTick(() => itemToDelete.value = null);
    dialogLoading.value = false;
  };

  const confirmDelete = async () => {
    if (!itemToDelete.value?.id) return;
    dialogLoading.value = true;
    try {
        await MaintenanceRequestService.delete(itemToDelete.value.id);
        mrs.value = mrs.value.filter(r => r.id !== itemToDelete.value.id);
        showSnackbar('Maintenance request deleted successfully!', 'success');
        closeDeleteDialog();
    } catch (error) {
        const errorMsg = error.response?.data?.error || 'Could not delete maintenance request.';
        showSnackbar(errorMsg, 'error', 6000);
        console.error("Error deleting maintenance request:", error.response || error);
    } finally {
        dialogLoading.value = false;
    }
  };

  const openViewDialog = async (item) => {
    viewDialogLoading.value = true;
    viewDialog.value = true;
    viewItem.value = null; 
    try {
      const fullRequestData = await MaintenanceRequestService.get(item.id);
      viewItem.value = fullRequestData;
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to load request details.';
      showSnackbar(errorMsg, 'error');
      console.error("Error fetching request details for view:", error);
      viewDialog.value = false; 
    } finally {
      viewDialogLoading.value = false;
    }
  };

  const closeViewDialog = () => {
    viewDialog.value = false;
  };
  </script>
  